/** API service for contact form and shared lead-capture path. */

import type { ContactFormData, ContactFormResponse, LeadCaptureInput, LeadSource } from '../types/contact';
import type {
  CalculationContextSnapshot,
  IntentSegment,
  MonetisationLeadRecord,
  WillingnessToPay,
} from '../types/monetisation';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export const LEAD_SOURCES = {
  CONSULTING: 'reverseTax-consulting',
  B2B: 'reverseTax-b2b',
} satisfies Record<string, LeadSource>;

export async function submitContactForm(data: ContactFormData): Promise<ContactFormResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/contact`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to send message' }));
    throw new Error(errorData.detail || 'Failed to send message');
  }

  return response.json();
}

/**
 * Shared lead-capture submission path (architecture §2).
 * Reuses the contact endpoint, tagged with a `source` so the backend can route
 * the lead to ERPNext (e.g. source = reverseTax-consulting creates an ERPNext Lead).
 */
export async function submitLead(data: LeadCaptureInput): Promise<ContactFormResponse> {
  const payload: ContactFormData = {
    name: data.name ?? '',
    email: data.email,
    subject: `Consulting enquiry (${data.source})`,
    message: data.message ?? 'Lead captured from the reverse-tax results consulting CTA.',
    source: data.source,
  };
  return submitContactForm(payload);
}

/**
 * Serializes the monetisation lead (segment, WTP, calc context) into a human-
 * readable message block for the lead-capture payload. The backend / ERPNext
 * stores this on the Lead tagged `reverseTax-b2b`.
 */
export function formatMonetisationLeadMessage(input: {
  segment: IntentSegment;
  willingnessToPay: WillingnessToPay;
  calculationContext?: CalculationContextSnapshot;
}): string {
  const ctx = input.calculationContext;
  const contextLines = ctx
    ? [
        `Desired net: R${ctx.desiredNetIncome.toFixed(2)} (${ctx.inputFrequency})`,
        `Required gross: R${ctx.grossIncome.toFixed(2)}`,
        `Achieved net: R${ctx.netIncome.toFixed(2)}`,
        ctx.taxYear ? `Tax year: ${ctx.taxYear}` : '',
      ].filter(Boolean)
    : [];

  return [
    'reverseTax B2B monetisation lead',
    `Segment: ${input.segment}`,
    `Willingness to pay: ${input.willingnessToPay}`,
    ...(contextLines.length > 0 ? ['Calculation context:', ...contextLines] : []),
  ].join('\n');
}

/**
 * Submits a monetisation lead (from the export intent gate) through the shared
 * lead-capture path, tagged `reverseTax-b2b` so ERPNext routes it as a B2B
 * lead. The monetisation dimensions + calculation context are serialized into
 * the message body (AC fx-2.2#AC2 / fx-2.2#AC3).
 */
export async function submitMonetisationLead(
  record: MonetisationLeadRecord,
): Promise<ContactFormResponse> {
  const payload: ContactFormData = {
    name: '',
    email: record.email,
    subject: `reverseTax B2B lead (${record.segment})`,
    message: formatMonetisationLeadMessage({
      segment: record.segment,
      willingnessToPay: record.willingnessToPay,
      calculationContext: record.calculationContext,
    }),
    source: LEAD_SOURCES.B2B,
  };
  return submitContactForm(payload);
}
